// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import React, {useMemo} from 'react';

import {WorkspaceDrawerScroller} from '../WorkspaceDrawerScroller';
import {FOOTER_ACTIONS_HEIGHT} from './constants';

type VirtuosoContext = {
  isFooterShown: boolean;
};

/**
 * Scroller component that conditionally reserves space for footer actions.
 */
export const Scroller = React.forwardRef<
  HTMLDivElement,
  {context?: VirtuosoContext}
>((props, ref) => {
  const footerHeight = props.context?.isFooterShown ? FOOTER_ACTIONS_HEIGHT : 0;
  return (
    <WorkspaceDrawerScroller ref={ref} footerHeight={footerHeight} {...props} />
  );
});

/**
 * This component is used as Header/Footer on the virtuoso list to ensure
 * proper top/bottom spacing. Unfortunately I wasn't able to figure out a
 * plain CSS solution that played nicely with Virtuoso. My first instinct
 * was to add top/bottom padding to the Scroller container, but that didn't
 * affect the position of the inner items. Then, I tried adding top/bottom
 * margin to the first/last items in the list. The first item correctly
 * displayed a top margin, but the last item never showed its bottom margin.
 * Next, I tried setting top/bottom padding on a custom List component,
 * but then the scroll stopped working. Finally I resorted to this solution.
 * Though unideal, it's the only option I found that actually works without
 * breaking something else.
 * Related: https://github.com/petyosi/react-virtuoso/issues/238
 */
export const TopBottomSpacing = () => <div className="h-8" />;

export const EmptyPlaceholder = () => (
  <div className="flex h-[calc(100%_-_16px)] w-full flex-col items-center justify-center text-moon-500">
    <div className="mb-16 flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-moon-300/[0.48]">
      <Icon name="search" className="h-24 w-24" />
    </div>
    <p className="font-semibold">No results found</p>
    <p className="text-sm">Try another search.</p>
  </div>
);

/**
 * Creates cached components/context for the Virtuoso list in SingleKeyPanelAdd
 */
export function useVirtuosoComponents({
  isFooterShown,
}: {
  isFooterShown: boolean;
}) {
  const components = useMemo(
    () => ({
      Scroller,
      EmptyPlaceholder,
      Header: TopBottomSpacing,
      Footer: TopBottomSpacing,
    }),
    []
  );

  const context = useMemo(
    () => ({
      isFooterShown,
    }),
    [isFooterShown]
  );

  return useMemo(
    () => ({
      components,
      context,
    }),
    [components, context]
  );
}
